import { useEffect, useState } from "react";

import { useParams, Navigate } from "react-router-dom";

import Categories from "../Categories";

import BlogCard from "../BlogCard";

const CategoryPage = () => {
  const [allBlogs, setAllBlogs] = useState([]);
  const [loading, setLoading] = useState(true); // For loading state
  const [error, setError] = useState(false); // For invalid category

  useEffect(() => {
    // Fetch the blogs.json file
    fetch("/blogs/blog.json")
      .then((response) => {
        if (!response.ok) {
          throw new Error("Network response was not ok");
        }
        return response.json();
      })
      .then((data) => {
        setAllBlogs(data);
        setLoading(false); // Data loaded
      })
      .catch((error) => {
        console.error("Error fetching blogs:", error);
        setError(true); // Set error state if fetch fails
      });
  }, []);

  const { slug: currentSlug } = useParams();

  // Generate categories from all blogs based on 'category' field
  const allCategories = ["all"]; // Initialize with 'all' category
  allBlogs.forEach((blog) => {
    const categorySlug = blog.category.toLowerCase();
    if (!allCategories.includes(categorySlug)) {
      allCategories.push(categorySlug);
    }
  });

  // Sort categories alphabetically but keeping "all" as the first
  allCategories.sort((a, b) => {
    if (a === "all") return -1; // Place "all" at the top
    if (b === "all") return 1;
    return a.localeCompare(b); // Sort other categories alphabetically
  });

  // Redirect to 404 if the slug is invalid
  if (!loading && !allCategories.includes(currentSlug)) {
    return <Navigate to="/404" />;
  }

  // Filter blogs based on the current category
  const blogs = allBlogs.filter((blog) => {
    if (currentSlug === "all") {
      return true; // Show all blogs if 'all' is selected
    }
    return blog.category.toLowerCase() === currentSlug;
  });

  if (loading) {
    return <div>Loading...</div>; // Show loading state
  }

  if (error) {
    return <div>Error loading blogs. Please try again later.</div>; // Show error state
  }

  const capitalizeSlug = (slug) => {
    return slug
      .split("-")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ");
  };

  // Capitalized slug
  const capitalizedSlug = capitalizeSlug(currentSlug);

  const pageTitle =
    currentSlug === "all"
      ? "Explore All Blogs | Insights and Articles by Bafar Labs"
      : `Discover ${capitalizedSlug} Blogs | Insights and Articles by Bafar Labs`;

  return (
    <article className="text-dark dark:text-light mt-24 flex flex-col pb-12">
      <title>{pageTitle}</title>
      <meta
        name="description"
        content={`Explore insightful blogs about ${currentSlug} and discover detailed articles to expand your knowledge. Browse more categories on Bafar Labs.`}
      />
      <meta
        name="keywords"
        content={`${currentSlug} blogs, ${currentSlug} insights, ${currentSlug} articles, ${currentSlug} category, Bafar Labs blogs, explore ${currentSlug}`}
      />
      <meta name="author" content="Bafar Labs" />
      <meta name="robots" content="index, follow" />
      <meta
        property="og:title"
        content={`Blogs on ${currentSlug} | Bafar Labs`}
      />
      <meta
        property="og:description"
        content={`Explore insightful blogs about ${currentSlug} and discover detailed articles to expand your knowledge. Browse more categories on Bafar Labs.`}
      />
      <meta
        property="og:url"
        content={`https://www.bafarlabs.com/blog-categories/${currentSlug}`}
      />
      <meta property="og:image" content="https://www.bafarlabs.com/logo.png" />
      <meta name="twitter:card" content="summary_large_image" />
      <meta
        name="twitter:title"
        content={`Blogs on ${currentSlug} | Bafar Labs`}
      />
      <meta
        name="twitter:description"
        content={`Explore insightful blogs about ${currentSlug} and discover detailed articles to expand your knowledge. Browse more categories on Bafar Labs.`}
      />
      <meta name="twitter:image" content="https://www.bafarlabs.com/logo.png" />
      <link
        rel="canonical"
        href={`https://www.bafarlabs.com/blog-categories/${currentSlug}`}
      />

      <h1 className="h1 mx-auto my-12 max-w-[50rem] transform text-center uppercase transition-all hover:scale-105 md:text-center">
        Blogs
      </h1>
      <div className="sxl:px-32 flex flex-col px-5 sm:px-10 md:px-24">
        <h3 className="text-2xl font-semibold md:text-4xl lg:text-5xl">
          #{currentSlug}
        </h3>
        <span className="mt-2 inline-block">
          Discover more categories and expand your knowledge!
        </span>
      </div>
      <Categories categories={allCategories} currentSlug={currentSlug} />

      <div className="sxl:mt-32 sxl:px-32 mt-5 grid grid-cols-1 grid-rows-2 gap-16 px-5 sm:mt-10 sm:grid-cols-2 sm:px-10 md:mt-24 md:px-24 lg:grid-cols-3">
        {blogs.map((blog, index) => (
          <BlogCard key={index} blog={blog} />
        ))}
      </div>
    </article>
  );
};

export default CategoryPage;
